import {
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  IconArrowUp,
  IconLoader2,
  IconMicrophone,
  IconPaperclip,
  IconPlayerStopFilled,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { isDesktopBrowserLayout } from "../../common/hooks/useIsDesktopView";
import { iosGenerateHaptic } from "../../tools/ios";
import { HapticEffect } from "../../interfaces";
import { InlineButton } from "./InlineButton";
import { ModePanel, ModeTrigger, type ModePanelProps } from "./ModePanel";

const chatInputVariants = cva(
  [
    // `z-20` is the card's, not the panel's: the sources panel opens upward out
    // of the card into the footer, where the jump-to-bottom button sits at
    // `z-10`. Its own `z-10` is trapped inside the card's stacking context, so
    // the card is what has to outrank the button.
    "relative z-20 flex",
    "border-[0.5px] border-solid",
    "transition-colors duration-150 ease-out",
    "focus-within:outline-none",
  ],
  {
    variants: {
      // The composer's SHAPE, not just its scale. `md` stacks the textarea
      // over its button row; `sm` is the maquette's "Mini" — one 52px row,
      // leading controls, field and actions side by side (Figma
      // "UX/UI JUISCI GPT", node 6690:6784). It exists for the article page,
      // where the composer is docked under a page it does not own and now
      // shares that band with the visual-abstract chip.
      size: {
        md: ["flex-col", "rounded-2xl"],
        sm: ["flex-row items-center gap-1", "min-h-13 rounded-2xl px-2 py-1.5"],
      },
      variant: {
        default: [
          "bg-[rgba(253,253,253,0.84)]",
          "backdrop-blur-[3px]",
          "border-transparent",
          "shadow-[0px_2px_8px_rgba(0,0,0,0.08)]",
          "focus-within:border-ds-gpt-composer-focus",
        ],
        // The desktop dock (Figma "D2.conversation.reponse"): opaque, outlined,
        // and three lines tall at rest, the field on top and the buttons at the
        // foot of the card.
        docked: [
          "bg-ds-gpt-composer",
          "border border-ds-black/4",
          "shadow-gpt-composer",
          "min-h-31 justify-between",
          "focus-within:border-ds-gpt-composer-focus",
        ],
        ghost: [
          "bg-transparent",
          "border-transparent",
          "shadow-none",
          "focus-within:border-ds-gpt-composer-focus",
        ],
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
);

/** Mode-selection control (Mode Router) shown in the button row. */
type ChatInputMode = Omit<ModePanelProps, "className">;

/** File-attachment affordance. The hidden picker lives here; the caller only
 *  receives what was chosen and reports whether an upload is still in flight. */
type ChatInputAttach = {
  /** `accept` list handed to the OS picker. */
  accept: string;
  /** An upload is in flight — the paperclip becomes a spinner and refuses taps. */
  isBusy: boolean;
  onPick: (files: FileList | null) => void;
};

/**
 * Presentational view of a dictation session. The caller maps its own state
 * machine onto these four — an error, for instance, reads as `idle`, because
 * the composer's job is only to offer the mic again.
 *
 * The mic is a plain toggle: it turns into a stop while listening and back into
 * a mic once stopped, so a session can be resumed as often as the user likes.
 * What was dictated simply stays in the composer as text, editable like any
 * other, which is why there is no discard half here.
 *
 * `transcribing` is the recorder path's beat between stop and the transcript
 * landing: nothing streamed while recording, so the card shows a spinner (and a
 * status line inside the card) until the text arrives — tappable to abandon the take.
 */
type ChatInputDictation =
  | { kind: "idle"; onStart: () => void }
  | { kind: "connecting"; onCancel: () => void }
  | { kind: "listening"; onStop: () => void }
  | { kind: "transcribing"; onCancel: () => void };

/** A turn in flight. `null` when the agent is not answering. */
type ChatInputStream =
  { kind: "streaming"; onStop: () => void } | { kind: "stopping" };

type ChatInputProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "children" | "value"
> &
  VariantProps<typeof chatInputVariants> & {
    value: string;
    /**
     * The Mode Router picker (docs/gpt-mode-router.md). `null` on surfaces where
     * there is nothing to choose between — the paper chat is grounded in one
     * paper, onboarding is not asking a medical question at all.
     */
    mode: ChatInputMode | null;
    /**
     * Extra control(s) at the HEAD of the leading button group, before the
     * attach/sources controls — e.g. a composer "+" menu that offers file AND
     * playlist referencing in place of the bare paperclip. `null` on surfaces
     * with nothing extra to lead with. Kept a generic slot so this primitive
     * stays unaware of the domain widget the caller composes.
     */
    leading: React.ReactNode;
    /**
     * What `leading` opens, on the same strip as the sources panel — above the
     * card, spanning its width. The card renders it because the card is what
     * that strip belongs to (and what dismisses it on an outside tap or
     * Escape); the caller owns whether it is open. `null` while it is not.
     * Only one panel shows at a time: opening this one puts the sources panel
     * away.
     */
    leadingPanel: {
      id: string;
      content: React.ReactNode;
      onDismiss: () => void;
      // `above` for a composer docked to the bottom of the screen. `below`
      // for one in the flow of a page with little room above it (the
      // desktop landing, where an upward panel is cut at the top).
      placement: "above" | "below";
    } | null;
    /**
     * A notice at the top of the card, above the field: the patient data
     * banner of the assistant engine. `null` on surfaces with nothing to say.
     * The `md` layout only: the `sm` row has no room for it.
     */
    banner: React.ReactElement | null;
    /** `null` on surfaces where nothing can be attached. */
    attach: ChatInputAttach | null;
    /** `null` where dictation is unsupported — the mic is then never offered. */
    dictation: ChatInputDictation | null;
    stream: ChatInputStream | null;
    onSend: () => void;
    /**
     * Something OTHER than an empty composer blocks sending (an upload in
     * flight, say). Emptiness is this component's own business: it holds the
     * value, so it refuses to send a blank message without being told.
     */
    sendDisabled: boolean;
  };

/** 15px font-size * 1.4 line-height = 21px per line */
const LINE_HEIGHT_PX = 21;
const MAX_LINES = 8;
/**
 * The compact row grows too, just far less: it is docked over a page the reader
 * is still reading, and eight lines of composer there would bury the article it
 * asks questions about.
 */
const MAX_LINES_COMPACT = 3;

const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  function ChatInput(
    {
      className,
      size,
      variant,
      value,
      mode,
      leading,
      leadingPanel,
      banner,
      attach,
      dictation,
      stream,
      onSend,
      sendDisabled,
      placeholder,
      onInput,
      onKeyDown,
      ...props
    },
    ref
  ) {
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const cardRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Open/closed is chrome, not domain state, so it stays inside this
    // primitive — the caller only owns which mode is selected.
    const [isModeOpen, setIsModeOpen] = useState(false);
    const modePanelId = useId();

    // Merged by hand rather than with `useImperativeHandle`: that hook wants an
    // `HTMLTextAreaElement`, and before mount there is none — the only way to
    // satisfy it is to lie about the null. A callback ref keeps both the local
    // handle (autosize, refocus) and the forwarded one honest.
    const attachRef = useCallback(
      (el: HTMLTextAreaElement | null) => {
        innerRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref !== null) ref.current = el;
      },
      [ref]
    );

    // Give the textarea the caret back. Called from the chrome around it, which
    // is not focusable and would otherwise leave the composer blurred.
    const focusTextarea = useCallback(() => {
      const el = innerRef.current;
      if (el === null) return;
      el.focus();
    }, []);

    const isCompact = size === "sm";
    const autoSize = useCallback(
      (el: HTMLTextAreaElement) => {
        el.style.height = "auto";
        const maxHeight =
          LINE_HEIGHT_PX * (isCompact ? MAX_LINES_COMPACT : MAX_LINES);
        el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
      },
      [isCompact]
    );

    // `onInput` only fires for user typing, so a `value` set programmatically —
    // dictation transcript, a prefilled initial question, an inserted follow-up
    // — would leave the textarea at its 1-row height and clip multi-line text
    // behind the actions row. Re-size whenever `value` changes.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (el !== null) autoSize(el);
    }, [value, autoSize]);

    // The leading panel's dismissal is the caller's to run, but the listeners
    // below outlive any one render of it — so reach for the current one rather
    // than closing over it (and re-subscribing on every render).
    const isLeadingPanelOpen = leadingPanel !== null;
    const dismissLeadingPanelRef = useRef<(() => void) | null>(null);
    dismissLeadingPanelRef.current =
      leadingPanel === null ? null : leadingPanel.onDismiss;

    // Dismiss whichever panel is open on an outside tap or Escape. Scoped to the
    // whole card, so toggling a switch — or putting the caret back in the
    // textarea — leaves it open; only leaving the composer closes it.
    useEffect(() => {
      if (!isModeOpen && !isLeadingPanelOpen) return;

      function dismiss(): void {
        setIsModeOpen(false);
        const dismissLeadingPanel = dismissLeadingPanelRef.current;
        if (dismissLeadingPanel !== null) dismissLeadingPanel();
      }
      function handlePointerDown(e: MouseEvent): void {
        const target = e.target;
        if (!(target instanceof Node)) return;
        if (cardRef.current !== null && cardRef.current.contains(target))
          return;
        dismiss();
      }
      function handleKeyDown(e: KeyboardEvent): void {
        if (e.key === "Escape") dismiss();
      }

      document.addEventListener("mousedown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [isModeOpen, isLeadingPanelOpen]);

    // The two panels share one strip, so opening the leading one PUTS the
    // sources panel away rather than hiding it — otherwise it would spring back
    // when the leading panel closes, which no one asked for. Close it before
    // paint so the trigger never shows both panels as open.
    useLayoutEffect(() => {
      if (isLeadingPanelOpen) setIsModeOpen(false);
    }, [isLeadingPanelOpen]);

    // A live dictation counts as content: the send waits for the transcript.
    const isListening = dictation !== null && dictation.kind === "listening";
    const isTranscribing =
      dictation !== null && dictation.kind === "transcribing";
    const canSend =
      (value.length > 0 || isListening || isTranscribing) &&
      !sendDisabled &&
      stream === null;

    // Escape stops the answer on desktop (Figma "Stop (Esc)"), wherever the
    // focus is, unless a dialog, a menu or a listbox takes it. A surface that
    // closes on the key marks it handled (`preventDefault`): its listener may
    // run first, and React removes the surface before this one runs, so an
    // open surface alone is no sign. One still open (a surface that does not
    // close on Escape) keeps the key too. A read-only composer takes no
    // command (the landing's inert hand-off shell draws a Stop it cannot
    // honour).
    const onStop =
      stream !== null && stream.kind === "streaming" && props.readOnly !== true
        ? stream.onStop
        : null;
    // While one of the composer's own panels is open, the key closes that
    // panel and nothing else. A field outside the composer (the sidebar's
    // search) keeps the key for itself too.
    useEffect(() => {
      if (
        onStop === null ||
        isModeOpen ||
        isLeadingPanelOpen ||
        !isDesktopBrowserLayout()
      )
        return;
      const stopOnEscape = (event: KeyboardEvent) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        if (
          document.querySelector(
            '[role="dialog"], [role="menu"], [role="listbox"]'
          ) !== null
        )
          return;
        const focused = document.activeElement;
        const card = cardRef.current;
        if (
          focused !== null &&
          focused.matches("input, textarea, select, [contenteditable]") &&
          (card === null || !card.contains(focused))
        )
          return;
        onStop();
      };
      document.addEventListener("keydown", stopOnEscape);
      return () => {
        document.removeEventListener("keydown", stopOnEscape);
      };
    }, [onStop, isModeOpen, isLeadingPanelOpen]);

    const send = useCallback(() => {
      if (!canSend) return;
      onSend();
    }, [canSend, onSend]);

    const handleInput: React.FormEventHandler<HTMLTextAreaElement> =
      useCallback(
        (e) => {
          autoSize(e.currentTarget);
          onInput?.(e);
        },
        [autoSize, onInput]
      );

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> =
      useCallback(
        (e) => {
          // Desktop web only: Enter sends, Shift+Enter inserts a newline.
          // Touch/native keep the default newline behavior (send via button),
          // also a native tablet with the desktop layout: its software
          // keyboard has no easy Shift+Enter.
          // isComposing guards against committing mid-IME composition.
          if (
            e.key === "Enter" &&
            !e.shiftKey &&
            !e.nativeEvent.isComposing &&
            isDesktopBrowserLayout()
          ) {
            e.preventDefault();
            send();
          }
          if (onKeyDown !== undefined) onKeyDown(e);
        },
        [send, onKeyDown]
      );

    // A live mic recolors the WHOLE card (tint + border) and swaps the mic
    // glyph for a stop — the recorder path streams no text while listening, so
    // the coloring is what says "recording" — while the button row keeps its
    // shape, so sending, attaching or dictating again are all one tap away from
    // a session. The transcribing beat that follows keeps only the border, with
    // the mic slot spinning and a status line inside the card until the
    // transcript lands.
    const dictationPlaceholder = isListening ? t("Listening…") : null;

    // The panel rises into place. Motion-sensitive users keep the reveal and
    // lose the travel, as everywhere else in the app.
    const panelHidden =
      prefersReducedMotion === true ? { opacity: 0 } : { opacity: 0, y: 8 };
    const panelVisible =
      prefersReducedMotion === true ? { opacity: 1 } : { opacity: 1, y: 0 };

    /**
     * The attach and mode controls, declared once because both layouts render
     * them: `sm` puts them at the head of the row, `md` at the head of the
     * button strip under the field.
     */
    const leadingControls = (
      <>
        {attach !== null && (
          <>
            <InlineButton
              state={attach.isBusy ? "pending" : "default"}
              aria-label={t("Attach a file")}
              onClick={() => {
                if (attach.isBusy) return;
                iosGenerateHaptic(HapticEffect.SELECTION);
                const input = fileInputRef.current;
                if (input === null)
                  throw new Error(
                    "should not happen: the file input is unmounted"
                  );
                input.click();
              }}
            >
              {attach.isBusy ? (
                <IconLoader2 className="animate-spin" />
              ) : (
                <IconPaperclip />
              )}
            </InlineButton>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={attach.accept}
              className="hidden"
              onChange={(e) => {
                attach.onPick(e.target.files);
                // Reset, so re-picking the SAME file fires `change` again.
                e.target.value = "";
              }}
            />
          </>
        )}
        {mode !== null && (
          <ModeTrigger
            selected={mode.selected}
            open={isModeOpen}
            controls={modePanelId}
            onClick={() => {
              // Taking the strip from the leading panel is an OPEN, not a
              // toggle: the tap asked for the mode picker, not for nothing.
              const dismissLeadingPanel = dismissLeadingPanelRef.current;
              if (dismissLeadingPanel !== null) {
                dismissLeadingPanel();
                setIsModeOpen(true);
                return;
              }
              setIsModeOpen(!isModeOpen);
            }}
          />
        )}
      </>
    );

    // The transcribing beat lasts a few seconds with no text moving, and a
    // placeholder hides as soon as the composer holds text: a status inside the
    // card says what the wait is, whatever the field holds. Inside, so it never
    // covers what sits above the composer (chips, a header): `md` gives it a
    // strip over the field, `sm` puts it in place of the leading controls.
    const transcribingStatus = isTranscribing ? (
      <div
        role="status"
        className={cn(
          "flex shrink-0 items-center gap-1.5",
          "font-['DM_Sans',sans-serif] text-sm text-ds-mint-700"
        )}
      >
        <IconLoader2 aria-hidden className="size-4 animate-spin" />
        <span>{t("Transcribing audio…")}</span>
      </div>
    ) : null;

    /**
     * The field itself, declared once for the same reason as `leadingControls`:
     * both layouts render the same textarea, only in a different place.
     */
    const field = (
      <textarea
        ref={attachRef}
        rows={1}
        value={value}
        placeholder={
          dictationPlaceholder !== null ? dictationPlaceholder : placeholder
        }
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        // Tapping into the composer to type dismisses the mode picker — the
        // choice is made from the trigger, and the panel shouldn't sit over the
        // text being written. (mousedown, not onFocus: it fires even when the
        // textarea already holds focus, e.g. the panel was opened from the pill.)
        onMouseDown={() => {
          setIsModeOpen(false);
        }}
        className={cn(
          "w-full resize-none bg-transparent overflow-hidden",
          // `min-w-0` only matters in the row: a textarea's intrinsic min-width
          // would otherwise refuse to shrink and push send off a narrow phone.
          isCompact ? "min-w-0 px-2 py-0" : "px-4 py-3",
          "font-['DM_Sans',sans-serif] text-[15px] leading-[1.4] font-normal",
          "text-ds-black",
          variant === "docked"
            ? "placeholder:text-ds-slate-700"
            : "placeholder:text-ds-slate-600",
          "focus:outline-none",
          "font-features-['salt'_1]"
        )}
        {...props}
      />
    );

    if (isCompact && banner !== null)
      throw new Error("should not happen: the compact composer has no banner");

    return (
      <div
        ref={cardRef}
        className={cn(
          chatInputVariants({ size, variant }),
          // The mint border spans the whole dictation session; the tint only
          // while audio is actually being recorded.
          (isListening || isTranscribing) && "border-ds-mint-700",
          isListening && "bg-ds-mint-100",
          className
        )}
        // Every touch drag that starts in the composer is a text gesture —
        // placing the caret, extending a selection, dragging a selection handle
        // — never a page scroll. The mobile shell dismisses the native keyboard
        // on any `touchmove` that reaches it (`dismissNativeKeyboardOnDrag` →
        // iOS `endEditing`, Android `hideSoftInputFromWindow`),
        // which tore the keyboard and the selection down mid-gesture: every
        // single time on a handle drag (WebKit emits a `touchmove` before its
        // own gesture recognizer takes over) and intermittently on the jitter of
        // a double-tap-to-select. The composer claims those drags for itself.
        onTouchMove={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Floats above the card rather than growing it: the composer is docked
            to the bottom of the screen, so the panel has to open upward, and it
            spans the card's full width to stay readable on mobile even though
            its trigger is a small pill. */}
        <AnimatePresence>
          {/* One strip, one panel: the leading control's takes precedence, since
              it is the one just tapped. */}
          {leadingPanel !== null ? (
            <motion.div
              key="leading-panel"
              id={leadingPanel.id}
              className={cn(
                "absolute inset-x-0 z-10",
                leadingPanel.placement === "above"
                  ? "bottom-full mb-3"
                  : "top-full mt-3"
              )}
              initial={panelHidden}
              animate={panelVisible}
              exit={panelHidden}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              {leadingPanel.content}
            </motion.div>
          ) : mode !== null && isModeOpen ? (
            <motion.div
              key="mode-panel"
              id={modePanelId}
              className="absolute inset-x-0 bottom-full z-10 mb-3"
              initial={panelHidden}
              animate={panelVisible}
              exit={panelHidden}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <ModePanel {...mode} />
            </motion.div>
          ) : null}
        </AnimatePresence>
        {/* One row or two. `sm` puts the field BETWEEN the two button groups,
            so the card is the row; `md` stacks the field over them. The
            controls themselves are identical — only where they sit changes. */}
        {isCompact ? (
          <>
            <div className="relative flex shrink-0 items-center gap-1">
              {leading}
              {transcribingStatus !== null ? transcribingStatus : leadingControls}
            </div>
            {field}
            <div className="relative flex shrink-0 items-center gap-1">
              <TrailingActions
                stop={variant === "docked" ? "round" : "pill"}
                stream={stream}
                dictation={dictation}
                hasText={value.length > 0}
                canSend={canSend}
                onSend={send}
              />
            </div>
          </>
        ) : (
          <>
            {banner !== null && <div className="px-2 pt-2">{banner}</div>}
            {transcribingStatus !== null && (
              <div className="px-4 pt-3">{transcribingStatus}</div>
            )}
            {field}
            <div className="relative flex items-center justify-between p-2">
              {/* The rounded card reads as ONE input, so a tap on the chrome around
              the textarea — the strip under the last line, the gaps beside the
              buttons — must not end the edit. That chrome is a plain div, and
              WebKit moves focus in the mousedown default action, so preventing
              it is what keeps the textarea focused and the mobile keyboard up;
              the tap then puts the caret back for the case where the composer
              was not focused yet. iOS gets its own `touchend` branch because it
              synthesises the mouse events too late to matter — preventing the
              tap's default there is what suppresses the blur, and it swallows
              the synthetic click, hence the explicit focus call in both.
              This layer sits BEHIND the action buttons (both groups are
              positioned, so they paint above it): mic and send keep their own
              tap behavior, which for dictation means blurring on purpose. */}
              <div
                aria-hidden
                className="absolute inset-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={focusTextarea}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  focusTextarea();
                }}
              />

              {/* Transitions of a state the user cannot see are announced instead. */}
              <span className="sr-only" aria-live="polite">
                {isListening ? t("Listening") : ""}
              </span>
              <div className="relative flex items-center gap-1">
                {leading}
                {leadingControls}
              </div>
              <div className="relative flex items-center gap-1">
                <TrailingActions
                  stop={variant === "docked" ? "round" : "pill"}
                  stream={stream}
                  dictation={dictation}
                  hasText={value.length > 0}
                  canSend={canSend}
                  onSend={send}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

/**
 * The right-hand button(s), which the maquette drives entirely from the state:
 * a turn in flight replaces send with stop (then a spinner), otherwise the mic
 * sits beside send, which darkens into the primary action once there is
 * something to send. The mic stays offered whatever the composer holds — that is
 * what lets a dictation be resumed at the caret after a stop and an edit.
 */
function TrailingActions({
  stop,
  stream,
  dictation,
  hasText,
  canSend,
  onSend,
}: {
  // `round`: the desktop dock's dark round stop, Escape bound (Figma
  // "Streaming"). `pill`: the phone's.
  stop: "round" | "pill";
  stream: ChatInputStream | null;
  dictation: ChatInputDictation | null;
  hasText: boolean;
  canSend: boolean;
  onSend: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (stream !== null) {
    switch (stream.kind) {
      case "streaming":
        switch (stop) {
          case "round":
            return (
              <InlineButton
                state="stop"
                aria-label={t("Stop")}
                aria-keyshortcuts="Escape"
                onClick={stream.onStop}
              >
                <span aria-hidden className="size-2.5 rounded-xs bg-current" />
              </InlineButton>
            );
          case "pill":
            return (
              <InlineButton
                state="pending"
                aria-label={t("Stop")}
                onClick={stream.onStop}
              >
                <IconPlayerStopFilled />
              </InlineButton>
            );
          default:
            throw new Error(stop satisfies never);
        }
      case "stopping":
        return (
          <InlineButton state="pending" aria-label={t("Stopping")}>
            <IconLoader2 className="animate-spin" />
          </InlineButton>
        );
      default:
        throw new Error(stream satisfies never);
    }
  }

  return (
    <>
      {dictation !== null && <DictationTrigger dictation={dictation} />}
      <InlineButton
        state={canSend ? "active" : hasText ? "disabled" : "resting"}
        aria-label={t("Send")}
        onClick={onSend}
      >
        <IconArrowUp />
      </InlineButton>
    </>
  );
}

/** The mic toggle, in all four of its faces: offer, connecting, the stop that
 *  ends a live session (leaving the transcript in the composer), and the
 *  transcribing spinner while a recorded take's transcript is in flight. */
function DictationTrigger({
  dictation,
}: {
  dictation: ChatInputDictation;
}): React.JSX.Element {
  const { t } = useTranslation();

  switch (dictation.kind) {
    case "idle":
      return (
        <InlineButton
          state="default"
          aria-label={t("Start dictation")}
          aria-pressed={false}
          onClick={dictation.onStart}
        >
          <IconMicrophone />
        </InlineButton>
      );
    case "connecting":
    case "transcribing":
      // One pending face for both waits (session coming up / recorded take's
      // transcript in flight). Clickable so the user can abandon rather than
      // wait it out — while connecting, the mic stream may already be live.
      return (
        <InlineButton
          state="pending"
          aria-label={t("Cancel dictation")}
          onClick={dictation.onCancel}
        >
          <IconLoader2 className="animate-spin" />
        </InlineButton>
      );
    case "listening":
      return (
        <InlineButton
          state="confirm"
          aria-label={t("Stop dictation")}
          aria-pressed={true}
          onClick={dictation.onStop}
        >
          <IconPlayerStopFilled />
        </InlineButton>
      );
    default:
      throw new Error(dictation satisfies never);
  }
}

export {
  ChatInput,
  chatInputVariants,
  type ChatInputProps,
  type ChatInputAttach,
  type ChatInputDictation,
  type ChatInputMode,
  type ChatInputStream,
};
